import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

interface Staff {
  id: number;
  name: string;
}

interface SchoolClass {
  id: number;
  name: string;
}

export interface Incident {
  id?: number;
  date: string;
  description: string;
  incident_type: string;
  severity: string;
  reported_by: number;
  target_class?: number;
  actions_taken?: string;
  responsible_name?: string;
  school_classes?: { id: number, name: string }[];
  is_active?: boolean;
}

interface IncidentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  incidentToEdit?: Incident | null;
}

const IncidentForm: React.FC<IncidentFormProps> = ({ onSuccess, onCancel, incidentToEdit }) => {
  const { addToast } = useToast();
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [incidentType, setIncidentType] = useState('Behavioral');
  const [severity, setSeverity] = useState('Low');
  const [reportedBy, setReportedBy] = useState<number | ''>('');
  const [targetClass, setTargetClass] = useState<number | ''>('');
  
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [allClasses, setAllClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [staffRes, classesRes] = await Promise.all([
          api.get(`staff/?active_only=true`),
          api.get(`classes/?active_only=true`)
        ]);
        setAllStaff(staffRes.data);
        setAllClasses(classesRes.data);
      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (incidentToEdit) {
      const d = new Date(incidentToEdit.date);
      setDate(d.toISOString().slice(0, 16));
      setDescription(incidentToEdit.description);
      setIncidentType(incidentToEdit.incident_type);
      setSeverity(incidentToEdit.severity);
      setReportedBy(incidentToEdit.reported_by);
      setTargetClass(incidentToEdit.target_class || '');
    } else {
      setDate(new Date().toISOString().slice(0, 16));
      setDescription('');
      setIncidentType('Behavioral');
      setSeverity('Low');
      setReportedBy('');
      setTargetClass('');
    }
  }, [incidentToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const incidentData = {
      date,
      description,
      incident_type: incidentType,
      severity,
      reported_by: reportedBy,
      target_class: targetClass || null
    };

    try {
      if (incidentToEdit && incidentToEdit.id) {
        await api.patch(`incidents/${incidentToEdit.id}/`, incidentData);
        addToast('Ocorrência atualizada.', 'success');
      } else {
        await api.post('incidents/', incidentData);
        addToast('Ocorrência registrada com sucesso.', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save incident', error);
      addToast('Erro ao registrar ocorrência.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{incidentToEdit ? '⚠️ Editar Registro' : '🚩 Registrar Ocorrência'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Classificação" icon="🏷️" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label>Tipo de Ocorrência</label>
            <select value={incidentType} onChange={(e) => setIncidentType(e.target.value)} required>
              <option value="Behavioral">Comportamental</option>
              <option value="Bullying">Bullying / Conflito</option>
              <option value="Academic">Acadêmica</option>
              <option value="Medical">Médica / Saúde</option>
              <option value="Other">Outra</option>
            </select>
          </div>

          <div className="form-group">
            <label>Gravidade</label>
            <select 
              value={severity} 
              onChange={(e) => setSeverity(e.target.value)} 
              required
              style={{ 
                color: severity === 'High' ? 'var(--danger)' : severity === 'Medium' ? 'var(--warning)' : 'var(--success)',
                fontWeight: '600'
              }}
            >
              <option value="Low">Baixa</option>
              <option value="Medium">Média</option>
              <option value="High">Alta / Crítica</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Relato e Data" icon="📄" />
        </div>

        <div className="form-group">
          <label>Data e Hora do Fato</label>
          <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>

        <div className="form-group">
          <label>Descrição Detalhada</label>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Descreva o ocorrido com o máximo de detalhes possível..."
            style={{ height: '120px' }}
            required
          />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Envolvidos e Relator" icon="👥" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Turma Relacionada</label>
            <select value={targetClass} onChange={(e) => setTargetClass(Number(e.target.value))}>
              <option value="">Nenhuma turma específica...</option>
              {allClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Quem Reportou</label>
            <select value={reportedBy} onChange={(e) => setReportedBy(Number(e.target.value))} required>
              <option value="">Selecione o colaborador...</option>
              {allStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn btn-orange" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Registro'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IncidentForm;
